import { Head } from "@shared/components/head";
import Button from "@shared/components/md-design-system/Button/Button";
import { View } from "react-native";
import { dynamicTranslations } from "@shared/utils/dynamicTranslation";
import { useForm } from "@shared/hooks";
import { Input } from "@shared/components/md-design-system/Input";
import { USER_FORM_INPUTS } from "../constants/userFormInputs";
import { IUserFormProps, IEmergencyContact } from "../types/userFormTypes";
import { useNavigation } from "@react-navigation/native";
import { userApi } from "../api/userApi";
import { useAuth } from "app/auth/hooks/useAuth";
import { ToastNotifications } from "../../../services/inAppToastNotifications";
import { Telemetry } from "app/services/telemetry/Telemetry";

const validatePhoneNumber = (phoneNumber: string): boolean => {
    if (!phoneNumber) return true;
    const cleanedPhone = phoneNumber.replace(/[\s\-\(\)]/g, '');
    return /^\d{8,15}$/.test(cleanedPhone);
};

export const UserDataForm = ({
    formType,
    initialValues,
    onSubmitSuccess,
    onSubmitError,
    onDataRefresh
}: IUserFormProps) => {
    const { title, inputs } = USER_FORM_INPUTS[formType];
    const { t } = dynamicTranslations();
    const navigation = useNavigation();
    const { user, meData } = useAuth();

    const { form, Form, formFields, submitForm, isSubmitting, PhoneInput } = useForm({
        inputs,
        initialValues,
        translate: t,
        onSubmit: async (values) => {
            try {
                if (!meData?.current_pathway.patient_id) {
                    throw new Error('User ID not found');
                }

                const phoneField = formType === 'personal_data'
                    ? values.phone_number
                    : values['emergency_contacts.0.phone_number'];

                if (phoneField && !validatePhoneNumber(phoneField)) {
                    const errorKey = formType === 'personal_data'
                        ? 'screens.profile.personalData.validation.phone-number.invalid'
                        : 'screens.profile.emergencyContact.validation.phone-number.invalid';
                    throw new Error(t(errorKey));
                }

                // TECHDEBT
                // Issue: https://app.clickup.com/t/86c8fp9eg
                // Description: Direct mutation without useMutation - missing cache invalidation, optimistic updates, and retry logic
                if (formType === 'personal_data') {
                    // Trasforma i dati per il formato API corretto
                    const transformedData = {
                        first_name: values.first_name,
                        last_name: values.last_name,
                        email: values.email,
                        phone: {
                            phone_number: values.phone_number,
                            country_code_prefix: values.phone_country_code_prefix
                        }
                    };
                    await userApi.updatePatientInformation(meData?.current_pathway.patient_id!, transformedData);
                } else if (formType === 'emergency_contact') {
                    const transformedData: IEmergencyContact = {
                        first_name: values['emergency_contacts.0.first_name'],
                        last_name: values['emergency_contacts.0.last_name'],
                        relationship: values['emergency_contacts.0.relationship'],
                        phone: {
                            phone_number: values['emergency_contacts.0.phone_number'],
                            country_code_prefix: values['emergency_contacts.0.country_code_prefix']
                        }
                    };
                    await userApi.updatePatientEmergencyContact(meData?.current_pathway.patient_id!, transformedData);
                }

                if (onDataRefresh) {
                    await onDataRefresh();
                }

                const successMessage = formType === 'personal_data'
                    ? t('screens.profile.personalData.successMessage')
                    : t('screens.profile.emergencyContact.successMessage');

                ToastNotifications.displaySuccessNotification(successMessage);

                onSubmitSuccess?.();
                navigation.goBack();
            } catch (error) {
                const errorMessage = formType === 'personal_data'
                    ? t('screens.profile.personalData.error')
                    : t('screens.profile.emergencyContact.error');

                ToastNotifications.displayErrorNotification(errorMessage);
                onSubmitError?.(error as Error);
            }
        },
        onValidationError: (errors) => {
            Telemetry.log('warning', "Validation errors:", { errors });
        }
    });

    return (
            <View className="grow flex-col justify-between gap-4 ">
                <View className="flex-col gap-4">
                    <Head type="h2">{t(title)}</Head>
                    <Form form={form}>
                        <View className='flex-col gap-4 bg-white pt-4'>
                            {formFields.map((field) => (
                                <View key={field.name} className='flex-col gap-2 border-white '>
                                    {field.type === 'phone' ? (
                                        <View>
                                            <Form.Item
                                                name={field.name}
                                                rules={field.rules}
                                                noStyle
                                            />
                                            {field.prefixName && (
                                                <Form.Item
                                                    name={field.prefixName}
                                                    rules={field.prefixRules || []}
                                                    noStyle
                                                />
                                            )}
                                            <PhoneInput {...field} />
                                        </View>
                                    ) : (
                                        <Form.Item
                                            name={field.name}
                                            rules={field.rules}
                                            noStyle
                                        >
                                            <Input {...field} />
                                        </Form.Item>
                                    )}
                                </View>
                            ))}
                        </View>
                    </Form>
                </View>
                <Button onPress={submitForm} disabled={isSubmitting}>
                    {isSubmitting
                        ? t(formType === 'personal_data'
                            ? "screens.profile.personalData.saving"
                            : "screens.profile.emergencyContact.saving"
                        )
                        : t(formType === 'personal_data'
                            ? "screens.profile.personalData.save"
                            : "screens.profile.emergencyContact.save"
                        )
                    }
                </Button>
            </View>
    );
};
